/**
 * Instagram-style Android hardware back-button handling.
 *
 * Pattern
 * -------
 * 1. Press back while inside the app  →  navigate router back (just like a
 *    browser/Instagram swipe-back). The user stays in the app.
 * 2. Press back on the root screen    →  show a "Press back again to exit"
 *    toast. A 2-second grace timer starts.
 * 3. Press back again within 2 seconds →  actually call App.exitApp().
 * 4. If the 2 seconds expire without another press, the timer resets and
 *    the next back press shows the toast again.
 *
 * Why this matters
 * ----------------
 * Without this, Android's hardware/system back button always exits the
 * Capacitor app — which feels broken to users who expect Instagram-style
 * in-app navigation.
 *
 * Plugins
 * -------
 * Uses `window.Capacitor.Plugins.App` (from `@capacitor/app`). The Android
 * build pipeline installs that plugin ad-hoc (same approach as status-bar
 * and navigation-bar). On the PWA / web build it's a no-op since there's
 * no hardware back to intercept.
 *
 * Usage
 * -----
 *   useHardwareBack();   // mount once at the App root inside the Router
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

// Pages where pressing back should trigger the exit-confirmation flow
// rather than navigating back further in router history.
const ROOT_PATHS = new Set(['/', '/dashboard', '/events', '/community', '/sk8-shop']);

const isNative = () =>
  typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

export const useHardwareBack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const exitArmedAt = useRef(0);
  const locationRef = useRef(location);

  // Keep the latest location available inside the listener closure
  // without re-subscribing on every navigation.
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    if (!isNative()) return;
    const App = window.Capacitor?.Plugins?.App;
    if (!App?.addListener) return;

    let handle;
    (async () => {
      handle = await App.addListener('backButton', () => {
        const path = locationRef.current?.pathname || '/';
        const atRoot = ROOT_PATHS.has(path) || window.history.length <= 1;

        if (!atRoot) {
          // Mid-flow: act like a browser back. RR's `navigate(-1)` works in
          // sync with the listener because Capacitor v7 ships its own
          // synthetic history.
          navigate(-1);
          return;
        }

        const now = Date.now();
        if (now - exitArmedAt.current < 2000) {
          // Second tap within 2 seconds → exit.
          try { App.exitApp(); } catch { /* no-op in web */ }
          return;
        }
        // First tap — arm exit and warn the user.
        exitArmedAt.current = now;
        toast('Press back again to exit', {
          duration: 1800,
          position: 'bottom-center',
        });
      });
    })();

    return () => {
      try {
        handle?.remove?.();
      } catch { /* ignore */ }
    };
  }, [navigate]);
};

export default useHardwareBack;
